import { useEffect, useCallback, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { fetchMultiplePokemon } from "../api/pokeapi";
import { shuffle } from "../utils/random";
import { generateDraftPool, expandPoolIfNeeded, filterByType } from "../utils/draftPool";
import { PokemonGrid } from "./PokemonGrid";
import { TwoPlayerTeams } from "./TwoPlayerTeams";
import { showToast } from "./Toast";
import { pokemonCache } from "../utils/cache";
import { aiPickPokemon } from "../utils/aiPlayer";
import { MAX_TEAM_SIZE } from "../types/pokemon";

export function DraftScreen() {
  const { state, dispatch } = useApp();
  const [cardGeneration, setCardGeneration] = useState(0);

  useEffect(() => {
    pokemonCache.lockSession();
    return () => pokemonCache.unlockSession();
  }, []);

  useEffect(() => {
    if (state.screen === "draft" && state.draftPool.length === 0) {
      const allDraftedIds = [
        ...state.teams[0].pokemon.map(p => p.id),
        ...state.teams[1].pokemon.map(p => p.id),
      ];
      const pool = generateDraftPool(state.settings, allDraftedIds);
      dispatch({ type: "SET_DRAFT_POOL", pool });
    }
  }, [state.screen, state.draftPool.length]);

  const generateCards = useCallback(async () => {
    if (state.draftPool.length === 0) return;
    dispatch({ type: "SET_LOADING", loading: true });
    try {
      const count = state.settings.cardsPerDraw;
      const allDraftedIds = [
        ...state.teams[0].pokemon.map(p => p.id),
        ...state.teams[1].pokemon.map(p => p.id),
      ];
      const { pool: expandedPool, message } = expandPoolIfNeeded(
        state.draftPool, state.settings, allDraftedIds, count
      );
      if (message) {
        showToast(message, "info");
        dispatch({ type: "SET_DRAFT_POOL", pool: expandedPool });
      }

      const poolToUse = message ? expandedPool : state.draftPool;
      const shuffled = shuffle([...poolToUse]);
      const fetchCount = state.settings.typeFilter ? count * 3 : count;
      const candidateIds = shuffled.slice(0, Math.min(fetchCount, shuffled.length));
      let pokemon = await fetchMultiplePokemon(candidateIds);
      pokemon = filterByType(pokemon, state.settings.typeFilter);

      dispatch({ type: "SET_DRAFT_CARDS", cards: pokemon.slice(0, count) });
      setCardGeneration(g => g + 1);
    } catch {
      dispatch({ type: "SET_ERROR", error: "Erreur lors du chargement des Pokemon" });
    }
  }, [state.draftPool, state.settings, state.teams, dispatch]);

  const prevPlayerRef = useRef(state.currentPlayer);
  useEffect(() => {
    if (state.screen !== "draft") return;
    if (state.draftCards.length === 0 && !state.loading) {
      generateCards();
      return;
    }
    if (prevPlayerRef.current !== state.currentPlayer) {
      prevPlayerRef.current = state.currentPlayer;
      generateCards();
    }
  }, [state.draftCards.length, state.loading, state.screen, state.currentPlayer, generateCards]);

  // AI auto-pick in solo mode
  const aiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (
      state.mode === "solo" &&
      state.currentPlayer === 2 &&
      state.draftCards.length > 0 &&
      !state.loading &&
      state.screen === "draft"
    ) {
      aiTimerRef.current = setTimeout(() => {
        const pick = aiPickPokemon(state.draftCards, state.teams[1].pokemon, state.aiDifficulty);
        dispatch({ type: "PICK_POKEMON", pokemon: pick });
        showToast(`IA a choisi ${pick.nameFr} !`, "info");
      }, 1200);
    }
    return () => {
      if (aiTimerRef.current) clearTimeout(aiTimerRef.current);
    };
  }, [state.mode, state.currentPlayer, state.draftCards, state.loading, state.screen]);

  const handleReroll = () => {
    const ri = state.currentPlayer === 1 ? 0 : 1;
    if (state.rerollsRemaining[ri] <= 0) {
      showToast("Plus de rerolls disponibles !", "warning");
      return;
    }
    dispatch({ type: "USE_REROLL" });
  };

  const handleBack = () => {
    const hasPicks = state.teams[0].pokemon.length > 0 || state.teams[1].pokemon.length > 0;
    if (hasPicks && !window.confirm("Abandonner le draft en cours ?")) return;
    dispatch({ type: "RESET_DRAFT" });
  };

  const rerollsLeft = state.rerollsRemaining[state.currentPlayer === 1 ? 0 : 1];
  const totalPicks = state.teams[0].pokemon.length + state.teams[1].pokemon.length;
  const progress = Math.round((totalPicks / (MAX_TEAM_SIZE * 2)) * 100);

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col relative overflow-hidden">

      {/* Top bar: back + player indicator + reroll */}
      <div className="flex items-center justify-between px-6 py-3 shrink-0"
        style={{
          background: "rgba(0,0,0,0.2)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}>
        <button
          onClick={handleBack}
          className="font-pokemon text-[10px] text-white/50 hover:text-white transition-colors flex items-center gap-1"
        >
          ← Retour
        </button>

        <div className="flex items-center gap-6">
          {/* Player indicator */}
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${state.currentPlayer === 1 ? "bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.6)]" : "bg-red-500/30"}`} />
            <span className={`font-pokemon text-xs transition-all ${state.currentPlayer === 1 ? "text-red-400 scale-110" : "text-white/30"}`}>
              J1
            </span>
            <span className="font-pokemon text-[10px] text-white/20">VS</span>
            <span className={`font-pokemon text-xs transition-all ${state.currentPlayer === 2 ? "text-blue-400 scale-110" : "text-white/30"}`}>
              {state.mode === "solo" ? "IA" : "J2"}
            </span>
            <div className={`w-3 h-3 rounded-full ${state.currentPlayer === 2 ? "bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.6)]" : "bg-blue-500/30"}`} />
          </div>

          {/* Progress bar */}
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-poke-yellow to-poke-gold transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="font-pokemon text-[9px] text-white/30">{totalPicks}/12</span>
          </div>
        </div>

        <button
          onClick={handleReroll}
          disabled={rerollsLeft <= 0 || state.loading}
          className="font-pokemon text-[10px] py-1.5 px-3 rounded-lg
                     bg-gradient-to-b from-poke-yellow/90 to-poke-yellow-dark/90
                     disabled:opacity-30 disabled:cursor-not-allowed
                     text-black shadow-lg transition-all hover:scale-105 active:scale-95"
        >
          Reroll ({rerollsLeft})
        </button>
      </div>

      {/* Main area: teams on sides, cards in center */}
      <div className="flex-1 flex items-center justify-center relative min-h-0">

        {/* Player 1 team - left */}
        <div className="shrink-0 w-[180px] h-full flex items-center pl-4">
          <TwoPlayerTeams player={1} />
        </div>

        {/* Center: Pokemon cards */}
        <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-4">
          {state.mode === "solo" && state.currentPlayer === 2 && (
            <div className="font-pokemon text-xs text-blue-400 mb-3 animate-pulse">
              IA reflechit...
            </div>
          )}

          <PokemonGrid
            pokemon={state.draftCards}
            loading={state.loading}
            generation={cardGeneration}
          />

          {state.error && (
            <p className="font-pokemon text-[10px] text-poke-red mt-2">{state.error}</p>
          )}
        </div>

        {/* Player 2 team - right */}
        <div className="shrink-0 w-[180px] h-full flex items-center pr-4">
          <TwoPlayerTeams player={2} />
        </div>
      </div>
    </div>
  );
}
